import React from 'react';
import { 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  ComposedChart,
  Area
} from 'recharts';

// Sample data - in a real app this would come from an API
const hourlyData = [
  { time: '00:00', tokens: 42000, cost: 8.40, responseTime: 510 },
  { time: '04:00', tokens: 38000, cost: 7.60, responseTime: 490 },
  { time: '08:00', tokens: 95000, cost: 19.00, responseTime: 550 },
  { time: '12:00', tokens: 185000, cost: 37.00, responseTime: 580 },
  { time: '16:00', tokens: 205000, cost: 41.00, responseTime: 530 },
  { time: '20:00', tokens: 145000, cost: 29.00, responseTime: 500 },
  { time: 'Now', tokens: 90000, cost: 18.00, responseTime: 520 },
];

interface CustomTooltipProps {
  active?: boolean;
  payload?: any[];
  label?: string;
}

const CustomTooltip: React.FC<CustomTooltipProps> = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="glass-panel-dark p-4">
        <p className="text-white/90 text-sm font-medium">{label}</p>
        {payload.map((entry: any, index: number) => (
          <p key={index} className="text-sm text-white/70">
            {entry.name === 'tokens' && `${(entry.value / 1000).toFixed(1)}k tokens`}
            {entry.name === 'cost' && `$${entry.value.toFixed(2)}`}
            {entry.name === 'responseTime' && `${entry.value}ms`}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

export default function DashboardCharts() {
  return (
    <div className="space-y-8">
      {/* Token Usage Chart */}
      <div className="glass-panel">
        <div className="px-8 py-6 border-b border-white/[0.05]">
          <h2 className="text-lg font-medium text-white">Token Usage Today</h2>
        </div>
        <div className="p-8" style={{ height: '300px' }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={hourlyData}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
              <XAxis 
                dataKey="time" 
                stroke="rgba(255,255,255,0.5)"
                tick={{ fill: 'rgba(255,255,255,0.5)' }}
              />
              <YAxis 
                stroke="rgba(255,255,255,0.5)"
                tick={{ fill: 'rgba(255,255,255,0.5)' }}
                tickFormatter={(value) => `${value / 1000}k`}
              />
              <Tooltip content={<CustomTooltip />} />
              <Line 
                type="monotone" 
                dataKey="tokens" 
                stroke="#8b5cf6" 
                strokeWidth={2}
                dot={{ fill: '#8b5cf6', strokeWidth: 2 }}
                activeDot={{ r: 6, fill: '#8b5cf6' }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Cost & Performance Chart */}
      <div className="glass-panel">
        <div className="px-8 py-6 border-b border-white/[0.05]">
          <h2 className="text-lg font-medium text-white">Cost & Performance</h2>
        </div>
        <div className="p-8" style={{ height: '300px' }}>
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={hourlyData}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
              <XAxis 
                dataKey="time" 
                stroke="rgba(255,255,255,0.5)"
                tick={{ fill: 'rgba(255,255,255,0.5)' }}
              />
              <YAxis 
                yAxisId="left"
                stroke="rgba(255,255,255,0.5)"
                tick={{ fill: 'rgba(255,255,255,0.5)' }}
                tickFormatter={(value) => `$${value}`}
              />
              <YAxis 
                yAxisId="right"
                orientation="right"
                stroke="rgba(255,255,255,0.5)"
                tick={{ fill: 'rgba(255,255,255,0.5)' }}
                tickFormatter={(value) => `${value}ms`}
              />
              <Tooltip content={<CustomTooltip />} />
              <Area
                yAxisId="left"
                type="monotone"
                dataKey="cost"
                fill="rgba(139, 92, 246, 0.1)"
                stroke="#8b5cf6"
                strokeWidth={2}
              />
              <Line
                yAxisId="right"
                type="monotone"
                dataKey="responseTime"
                stroke="#10b981"
                strokeWidth={2}
                dot={{ fill: '#10b981', strokeWidth: 2 }}
                activeDot={{ r: 6, fill: '#10b981' }}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}
